import { PointOfInterestType } from '@/models/enums';
import { CwError, createCwError } from '@/lib/helpers/errors';

import type { FcwPointOfInterestSheetProps } from '@/components/organisms/FcwPointOfInterestSheet.vue';

interface UsePointsOfInterest {
  /**
   * The selected point of interest
   */
  selectedPointOfInterest: Ref<PointOfInterest | undefined>;
  /**
   * Props to use in point of interest sheet
   */
  selectedPointOfInterestSheetProps: ComputedRef<
    Omit<FcwPointOfInterestSheetProps, 'modelValue'>
  >;
  /**
   * Select a point of interest on the map
   * @param id - ID of the point of interest to select
   */
  selectPointOfInterest: (id: string | null) => Promise<void>;
  /**
   * Fetch a point of interest data, by ID
   * @param id - ID of the point of interest to fetch
   */
  fetchPointOfInterest: (id: string) => Promise<void>;
  /**
   * ID of the selected point of interest
   */
  selectedPointOfInterestId: Ref<string | null>;
  /**
   * Whether the point of interest data is loading
   */
  isLoading: Ref<boolean>;
  /**
   * Last updated time
   */
  lastUpdatedAt: Ref<Date | null>;
  /**
   * Current error
   */
  error: Ref<CwError | undefined>;
}

export function usePointsOfInterest(
  pointsOfInterest: Ref<MapPointOfInterest[]>
): UsePointsOfInterest {
  const api = useApi();
  const error = ref<CwError>();

  const isLoading = ref();

  const lastUpdatedAt = ref<Date | null>(null);

  const selectedPointOfInterest = ref<PointOfInterest>();

  const selectedPointOfInterestId = ref<string | null>(null);
  const selectedMapPointOfInterest = computed(() =>
    pointsOfInterest.value.find(
      pointOfInterest => pointOfInterest.id === selectedPointOfInterestId.value
    )
  );

  const selectedPointOfInterestCoordinates = computed(
    () => selectedMapPointOfInterest.value?.coordinates as Coordinates
  );

  const selectedPointOfInterestSheetProps = computed<
    Omit<FcwPointOfInterestSheetProps, 'modelValue'>
  >(() => ({
    name: selectedMapPointOfInterest?.value?.label ?? '',
    address: {
      lines: selectedPointOfInterest.value?.address ?? [],
      coordinates: {
        lat: selectedPointOfInterestCoordinates.value?.[1] ?? 0,
        lng: selectedPointOfInterestCoordinates.value?.[0] ?? 0,
      },
    },
    ctaLink: selectedMapPointOfInterest?.value?.ctaLink ?? '',
    type:
      selectedMapPointOfInterest?.value?.type ?? PointOfInterestType.Unknown,
  }));

  async function fetchPointOfInterest(): Promise<void> {
    const { data: pointOfInterestData, error: pointOfInterestError } =
      await api.get(
        `/client/entities/points-of-interest/${selectedPointOfInterestId.value}`
      );

    if (pointOfInterestError.value) {
      error.value = createCwError(pointOfInterestError.value.errorCode);
    }

    selectedPointOfInterest.value = pointOfInterestData.value?.pointOfInterest;
    lastUpdatedAt.value = new Date();
  }

  async function selectPointOfInterest(id: string | null): Promise<void> {
    selectedPointOfInterestId.value = id;

    if (!selectedPointOfInterestId.value) return;

    isLoading.value = true;
    await fetchPointOfInterest();
    isLoading.value = false;
  }

  return {
    selectedPointOfInterest,
    selectedPointOfInterestSheetProps,
    selectPointOfInterest,
    selectedPointOfInterestId,
    fetchPointOfInterest,
    isLoading,
    lastUpdatedAt,
    error,
  };
}
